import { useState } from 'react';
import Banner from './sections/PageBanner';
import PageHeader from './sections/PageHeader';
import Footer from './sections/PageFooter';

/* ── Section data ───────────────────────────────────────────────────── */

const SECTIONS = [
  {
    id: 'overview',
    title: 'Platform Overview',
    icon: '🌍',
    content: [
      {
        heading: 'What is WITS?',
        body: `The Wildlife Information & Tracking System (WITS) is an integrated intelligence platform built to support wildlife crime prosecution and conservation enforcement across Africa. It consolidates case data, offender profiles, judicial records, and geospatial intelligence into a single, secure environment accessible to rangers, prosecutors, conservationists, and policy makers.`,
      },
      {
        heading: 'Who is it for?',
        body: `WITS serves four primary audiences: law enforcement agencies tracking active wildlife crime cases; prosecutors building evidence trails and monitoring judicial outcomes; conservation organisations mapping species and trafficking corridors; and policy makers analysing trends to close legal gaps.`,
      },
      {
        heading: 'Data coverage',
        body: `The platform currently covers seizure cases, court proceedings, offender records, and incident reports primarily from East and Southern Africa, with data contributed by partner organisations and field reporters.`,
      },
    ],
  },
  {
    id: 'modules',
    title: 'Core Modules',
    icon: '🧩',
    content: [
      {
        heading: 'Module A — Case Tracking & Management',
        body: `Centralised, searchable database of wildlife crime cases from arrest to verdict. Each record captures accused details, charges, species, seizure location and date, court, judgment outcome, sentencing, and case status. Admins can upload datasets via Excel or merge field incident reports directly into the case database.`,
      },
      {
        heading: 'Module B — Analytics & Reporting',
        body: `Converts raw case records into actionable intelligence. Includes conviction rate tracking, species distribution charts, year-on-year seizure trends, judicial performance by court, sentencing analysis (fine ranges, imprisonment lengths, acquittal rates), and stakeholder report generation with snapshot timelines.`,
      },
      {
        heading: 'Module C — Geographical Mapping',
        body: `Interactive map powered by Leaflet with two display modes — Pins (individual case markers with status colour coding) and Heat (crime density heatmap). Three optional overlay layers: Protected Areas (GIS shapefile of Eastern Africa conservation zones), Trafficking Routes (computed from multi-location offender records), and Migration Corridors (major wildlife movement routes).`,
      },
      {
        heading: 'Module D — Incident Reporting',
        body: `Public-facing reporting form accessible at /report-incident. Accepts location, crime type, agency notification preferences, and detailed descriptions from rangers, community members, and whistleblowers. Reports are reviewed by administrators who can add notes, update status, and merge approved reports into the main case database.`,
      },
    ],
  },
  {
    id: 'intelligence',
    title: 'Intelligence Views',
    icon: '🔍',
    content: [
      {
        heading: 'Offender Intelligence',
        body: `Profiles all accused individuals across cases. Computes a threat risk score (HIGH / MEDIUM / LOW) based on case count, species involvement, and absconded status. Detects suspected criminal syndicates using a BFS connected-components algorithm on shared-case adjacency. Shows an activity timeline sparkline and species profile for each offender. Nationality bars are clickable filters.`,
      },
      {
        heading: 'Network Analysis',
        body: `Visualises relationships between cases, offenders, locations, and species as an interactive force-directed graph. Nodes represent entities; edges represent connections. Clicking a node isolates its sub-network. Useful for identifying trafficking hubs and syndicate structures invisible in tabular data.`,
      },
      {
        heading: 'Species Watch',
        body: `Monitors specific species of concern across the dataset. Shows case counts, seizure trends, geographic distribution, and associated offender nationalities per species. Supports conservation prioritisation and CITES reporting.`,
      },
      {
        heading: 'Sentencing Intelligence',
        body: `Analyses judicial outcomes across four tabs: Overview (outcome distribution, fine and imprisonment statistics), By Species (acquittal and imprisonment rates per species), By Court (conviction and acquittal rates per court with performance bars), and Trends (stacked year-by-year outcome chart).`,
      },
      {
        heading: 'Case Delay Monitor',
        body: `Identifies ongoing cases that have exceeded normal processing time. Cases are bucketed by duration: Watch (<1 yr), Medium (1–2 yr), High (2–5 yr), Critical (5+ yr). Sortable by age, species, or court. Triggers a fugitive alert banner when critical cases are detected. Supports judicial accountability reporting.`,
      },
    ],
  },
  {
    id: 'access',
    title: 'Access & Roles',
    icon: '🔐',
    content: [
      {
        heading: 'Guest (unauthenticated)',
        body: `Access to the public site: Home, About, Features, Case Precedents, Get Involved, FAQs, Contact, API Documentation, and this page. Inside the dashboard, guests can view the Map Intelligence and Species Watch views and submit a data export request. No case data is editable.`,
      },
      {
        heading: 'User (authenticated)',
        body: `Full access to all intelligence views: Map, Network Analysis, Analytics, Species Watch, Offender Intelligence, Sentencing Intelligence, Case Delay Monitor. Can view the Dataset, request data exports, generate and save intelligence reports (My Report), manage personal API keys, and submit incident reports.`,
      },
      {
        heading: 'Admin',
        body: `All User capabilities plus: uploading new datasets via Excel, reviewing and merging incident reports, managing case precedents (create, edit), managing articles and news, and exporting data. Cannot manage other user accounts.`,
      },
      {
        heading: 'Super Admin',
        body: `All Admin capabilities plus: user account management (role assignment, suspension), full system health monitoring, and unrestricted data access across all modules.`,
      },
    ],
  },
  {
    id: 'api',
    title: 'Developer API',
    icon: '⚙️',
    content: [
      {
        heading: 'Authentication',
        body: `The WITS REST API uses API key authentication. Include your key in the X-Api-Key header on every request. Keys are generated from the API Keys section inside your dashboard and are shown in plaintext only once at creation. Store them securely.`,
      },
      {
        heading: 'Rate limiting',
        body: `All API keys are rate-limited to 60 requests per minute using a sliding-window algorithm. The current usage count and remaining quota are returned in X-RateLimit-Limit, X-RateLimit-Remaining, and X-RateLimit-Reset response headers. Exceeding the limit returns HTTP 429.`,
      },
      {
        heading: 'Key endpoints',
        body: `GET /api/v1/seizure-cases/all-cases — full case dataset. GET /api/v1/precedents/all — published case precedents. GET /api/v1/species — species list. All responses are JSON. Full interactive documentation is available at /api-documentation.`,
      },
      {
        heading: 'Example request',
        body: `curl -H "X-Api-Key: wits_your_key_here" https://wits-api-55ec437f121b.herokuapp.com/api/v1/seizure-cases/all-cases`,
      },
    ],
  },
  {
    id: 'reporting',
    title: 'Reporting an Incident',
    icon: '📋',
    content: [
      {
        heading: 'How to report',
        body: `Visit /report-incident and complete the form. Provide your name and contact details (optional for anonymous submissions), the location of the incident, the crime type, which agency should be notified, and a detailed description. The form is protected and submissions are encrypted in transit.`,
      },
      {
        heading: 'What happens next',
        body: `Your report is received by WITS administrators who review it within 48 hours. Verified reports are tagged with a status (Pending → Reviewed → Merged). If you provided contact details, you may receive follow-up feedback. Approved reports are merged into the case database and contribute to the intelligence views.`,
      },
      {
        heading: 'Confidentiality',
        body: `Reporter identity is never shared outside the WITS administrative team. Anonymous submissions are fully supported — simply leave the name and contact fields blank. WITS operates under a strict whistleblower protection policy in line with the organisations that fund and govern the platform.`,
      },
    ],
  },
];

/* ── Sidebar nav item ───────────────────────────────────────────────── */

function SidebarItem({ section, active, onClick }: { section: typeof SECTIONS[0]; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl transition-all text-sm ${
        active
          ? 'bg-[#052e16] text-green-400 font-semibold'
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
      }`}
      style={{ fontFamily: 'var(--font-body)' }}
    >
      <span className="text-base leading-none shrink-0">{section.icon}</span>
      {section.title}
    </button>
  );
}

/* ── Content block ──────────────────────────────────────────────────── */

function ContentBlock({ heading, body }: { heading: string; body: string }) {
  return (
    <div className="mb-8 last:mb-0">
      <h3
        className="text-base font-semibold text-gray-900 mb-2"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        {heading}
      </h3>
      {body.startsWith('curl') ? (
        <pre
          className="bg-gray-900 text-green-400 text-xs rounded-xl px-5 py-4 overflow-x-auto leading-relaxed"
          style={{ fontFamily: 'monospace' }}
        >
          {body}
        </pre>
      ) : (
        <p className="text-gray-600 text-sm leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
          {body}
        </p>
      )}
    </div>
  );
}

/* ── Page hero ──────────────────────────────────────────────────────── */

function PageHero() {
  return (
    <div
      className="relative w-full overflow-hidden flex flex-col justify-end"
      style={{ height: '40vh', minHeight: 280, background: '#052e16' }}
    >
      <img
        src="/images/v1/img12.jpg"
        alt=""
        className="absolute inset-0 w-full h-full object-cover select-none"
        style={{ opacity: 0.22 }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#052e16]/20 via-transparent to-[#052e16]/95 pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pb-12">
        <span className="flex items-center gap-3 mb-4">
          <span className="h-px w-8 bg-green-400" />
          <span className="text-green-400 text-xs tracking-[0.22em] uppercase font-medium" style={{ fontFamily: 'var(--font-body)' }}>
            Platform Documentation
          </span>
        </span>
        <h1
          className="text-5xl lg:text-6xl font-bold text-white leading-tight"
          style={{ fontFamily: 'var(--font-display)', textShadow: '0 2px 24px rgba(0,0,0,0.5)' }}
        >
          How <span className="text-green-400">WITS</span> Works
        </h1>
        <p
          className="mt-4 text-white/65 text-base max-w-xl leading-relaxed"
          style={{ fontFamily: 'var(--font-body)', textShadow: '0 1px 8px rgba(0,0,0,0.4)' }}
        >
          A complete guide to the platform — modules, intelligence views, roles, API access, and incident reporting.
        </p>
      </div>
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────── */

export default function PlatformDocs() {
  const [active, setActive] = useState('overview');
  const section = SECTIONS.find((s) => s.id === active) ?? SECTIONS[0];

  return (
    <>
      <Banner />
      <PageHeader />
      <PageHero />

      <div className="w-full bg-[#fafaf7] min-h-screen">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <div className="flex flex-col lg:flex-row gap-10">

            {/* Sidebar */}
            <aside className="lg:w-60 shrink-0">
              <div className="sticky top-24 flex flex-col gap-1 bg-white rounded-2xl border border-gray-100 p-3 shadow-sm">
                <p
                  className="text-[10px] font-semibold text-gray-400 uppercase tracking-[0.15em] px-4 pt-2 pb-1"
                  style={{ fontFamily: 'var(--font-body)' }}
                >
                  Contents
                </p>
                {SECTIONS.map((s) => (
                  <SidebarItem
                    key={s.id}
                    section={s}
                    active={active === s.id}
                    onClick={() => setActive(s.id)}
                  />
                ))}
              </div>
            </aside>

            {/* Main content */}
            <main className="flex-1 min-w-0">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-8 py-10 lg:px-12">

                {/* Section header */}
                <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-100">
                  <span className="text-3xl leading-none">{section.icon}</span>
                  <h2
                    className="text-2xl font-bold text-gray-900"
                    style={{ fontFamily: 'var(--font-display)' }}
                  >
                    {section.title}
                  </h2>
                </div>

                {/* Content blocks */}
                {section.content.map((block) => (
                  <ContentBlock key={block.heading} heading={block.heading} body={block.body} />
                ))}

                {/* Pagination */}
                <div className="flex items-center justify-between pt-8 mt-8 border-t border-gray-100">
                  {(() => {
                    const idx = SECTIONS.findIndex((s) => s.id === active);
                    const prev = SECTIONS[idx - 1];
                    const next = SECTIONS[idx + 1];
                    return (
                      <>
                        {prev ? (
                          <button
                            onClick={() => setActive(prev.id)}
                            className="flex items-center gap-2 text-sm text-gray-500 hover:text-green-700 transition-colors"
                            style={{ fontFamily: 'var(--font-body)' }}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                            {prev.title}
                          </button>
                        ) : <span />}
                        {next ? (
                          <button
                            onClick={() => setActive(next.id)}
                            className="flex items-center gap-2 text-sm text-gray-500 hover:text-green-700 transition-colors ml-auto"
                            style={{ fontFamily: 'var(--font-body)' }}
                          >
                            {next.title}
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        ) : <span />}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Quick links footer */}
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { label: 'Live Intelligence', href: '/dashboard', desc: 'Open the dashboard' },
                  { label: 'API Documentation', href: '/api-documentation', desc: 'Full API reference' },
                  { label: 'Report an Incident', href: '/report-incident', desc: 'Submit a field report' },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="flex flex-col gap-1 bg-white rounded-xl border border-gray-100 px-5 py-4 hover:border-green-200 hover:shadow-sm transition-all group"
                  >
                    <span className="text-sm font-semibold text-gray-800 group-hover:text-green-700 transition-colors" style={{ fontFamily: 'var(--font-display)' }}>
                      {link.label} →
                    </span>
                    <span className="text-xs text-gray-400" style={{ fontFamily: 'var(--font-body)' }}>{link.desc}</span>
                  </a>
                ))}
              </div>
            </main>

          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}
